import { MemberStatus, MembershipType } from '@/types';

export function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString('pl-PL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatDateShort(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString('pl-PL');
}

export function formatDateTime(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleString('pl-PL');
}

export function toInputDateFormat(date: Date | string): string {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getStatusLabel(status: MemberStatus): string {
  const labels: Record<MemberStatus, string> = {
    active: 'Aktywny',
    inactive: 'Nieaktywny',
    suspended: 'Zawieszony',
    removed: 'Usunięty',
  };
  return labels[status] || status;
}

export function getStatusColor(status: MemberStatus): string {
  const colors: Record<MemberStatus, string> = {
    active: 'bg-green-500/20 text-green-400 ring-1 ring-green-500/30',
    inactive: 'bg-zinc-500/20 text-zinc-400 ring-1 ring-zinc-500/30',
    suspended: 'bg-yellow-500/20 text-yellow-400 ring-1 ring-yellow-500/30',
    removed: 'bg-red-500/20 text-red-400 ring-1 ring-red-500/30',
  };
  return colors[status] || 'bg-zinc-500/20 text-zinc-400 ring-1 ring-zinc-500/30';
}

// Check if membership is expired based on expiryDate
export function isPaymentExpired(expiryDate?: Date | string): boolean {
  if (!expiryDate) return false;
  return new Date(expiryDate) < new Date();
}

// Get combined status label with payment info
export function getCombinedStatusLabel(status: MemberStatus, expiryDate?: Date | string): string {
  const baseLabel = getStatusLabel(status);
  const expired = isPaymentExpired(expiryDate);

  if (status === 'active' && expired) {
    return 'Aktywny - nieopłacony';
  }
  if (status === 'active' && !expired) {
    return 'Aktywny - opłacony';
  }
  return baseLabel;
}

// Get combined status color with payment info
export function getCombinedStatusColor(status: MemberStatus, expiryDate?: Date | string): string {
  const expired = isPaymentExpired(expiryDate);

  if (status === 'active' && expired) {
    return 'bg-red-500/20 text-red-400 ring-1 ring-red-500/30';
  }
  if (status === 'active' && !expired) {
    return 'bg-green-500/20 text-green-400 ring-1 ring-green-500/30';
  }

  return getStatusColor(status);
}

export function getMembershipTypeLabel(type: MembershipType): string {
  const labels: Record<MembershipType, string> = {
    regular: 'Członek zwyczajny',
    honorary: 'Członek honorowy',
  };
  return labels[type] || type;
}

export function getFullName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`.trim();
}

export function formatPesel(pesel: string): string {
  // Mask PESEL for privacy: show only first 2 and last 2 digits
  if (pesel.length === 11) {
    return `${pesel.slice(0, 2)}*******${pesel.slice(-2)}`;
  }
  return pesel;
}

/**
 * Replace Polish diacritics with ASCII equivalents.
 * Useful for file names and other contexts where Unicode is not supported.
 */
export function sanitizePolishChars(text: string): string {
  const map: Record<string, string> = {
    'ą': 'a', 'Ą': 'A', 'ć': 'c', 'Ć': 'C', 'ę': 'e', 'Ę': 'E',
    'ł': 'l', 'Ł': 'L', 'ń': 'n', 'Ń': 'N', 'ó': 'o', 'Ó': 'O',
    'ś': 's', 'Ś': 'S', 'ź': 'z', 'Ź': 'Z', 'ż': 'z', 'Ż': 'Z',
  };
  return text.replace(/[ąĄćĆęĘłŁńŃóÓśŚźŹżŻ]/g, ch => map[ch] || ch);
}

export function formatDateSafe(date: string | null | undefined): string {
  if (!date) return '-';
  try {
    return formatDateShort(date);
  } catch {
    return date;
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatPhone(phone: string): string {
  // Remove all non-digit characters except +
  let cleaned = phone.replace(/[^\d+]/g, '');

  // Convert 00 prefix to + (international dialing format)
  if (cleaned.startsWith('00')) {
    cleaned = '+' + cleaned.slice(2);
  }

  // Extract digits only for formatting
  const digits = cleaned.replace(/\D/g, '');

  // Handle Polish numbers
  if (digits.length === 9) {
    // 9 digits - Polish number without country code
    return `+48 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits.startsWith('48')) {
    // 48 + 9 digits - Polish number with country code
    const local = digits.slice(2);
    return `+48 ${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;
  }

  // For other formats, just return with + prefix if missing
  if (cleaned.startsWith('+')) {
    return cleaned;
  }
  return `+48 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

